import axios from "axios";

/**
 * Fuel surcharge API client (js_crm_backend docs/pricing/FUEL_SURCHARGE_AND_MINIMUMS_SCOPE.md).
 * Settings, provider overrides and client waivers are company-only; the
 * `active` flag (for the Instant Estimate notice) is readable by clients
 * and never carries the % or an amount.
 */

export const FUEL_SURCHARGE_TYPE = "fuel_surcharge";

export interface FuelSurchargeSettings {
  enabled: boolean;
  percent: number;
  markupPercent: number;
  notice: string;
  noticeCustom?: boolean;
}
export interface ProviderSurchargeOverride {
  providerId: string;
  providerName?: string | null;
  percent: number;
  markupPercent: number | null;
}
export interface OpenQuoteRow {
  quoteId: string;
  sequence: number;
  majorVersion: number;
  minorVersion: number;
  projectName: string | null;
  clientName: string | null;
  expiresAt: string | null;
  total: number;
  wouldAdd: number;
}

type Err = { error: string };
const fail = (e: any, what: string): Err => {
  const d = e?.response?.data;
  const m = d?.message || d?.error;
  return { error: Array.isArray(m) ? m.join(", ") : m ? String(m) : `${what} failed.` };
};

export const getFuelSurchargeSettings = async (): Promise<{ settings: FuelSurchargeSettings; providers: ProviderSurchargeOverride[]; waivedClients: Array<{ clientId: string; name: string }> } | Err> => {
  try {
    const res = await axios.get(`/fuel-surcharge/settings`, { excludeInterceptor: true } as any);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the fuel surcharge settings");
  }
};

export const updateFuelSurchargeSettings = async (input: Partial<FuelSurchargeSettings>): Promise<FuelSurchargeSettings | Err> => {
  try {
    const res = await axios.put(`/fuel-surcharge/settings`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving the fuel surcharge settings");
  }
};

export const setProviderSurcharge = async (providerId: string, input: { percent: number; markupPercent?: number | null }): Promise<ProviderSurchargeOverride[] | Err> => {
  try {
    const res = await axios.put(`/fuel-surcharge/providers/${providerId}`, input);
    return res?.data ?? [];
  } catch (e: any) {
    return fail(e, "Saving the provider override");
  }
};

export const clearProviderSurcharge = async (providerId: string): Promise<ProviderSurchargeOverride[] | Err> => {
  try {
    const res = await axios.delete(`/fuel-surcharge/providers/${providerId}`);
    return res?.data ?? [];
  } catch (e: any) {
    return fail(e, "Removing the provider override");
  }
};

export const setClientSurchargeWaived = async (clientId: string, waived: boolean): Promise<any | Err> => {
  try {
    const res = await axios.put(`/fuel-surcharge/clients/${clientId}`, { waived });
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, "Saving the client waiver");
  }
};

export const getOpenQuotesWithoutSurcharge = async (): Promise<{ enabled: boolean; quotes: OpenQuoteRow[] } | Err> => {
  try {
    const res = await axios.get(`/fuel-surcharge/open-quotes`);
    return res?.data ?? { enabled: false, quotes: [] };
  } catch (e: any) {
    return fail(e, "Loading open quotes");
  }
};

export const applySurchargeToOpenQuotes = async (quoteIds?: string[]): Promise<{ applied: number; skipped: number; failed: number } | Err> => {
  try {
    const res = await axios.post(`/fuel-surcharge/open-quotes/apply`, { quoteIds });
    return res?.data ?? { applied: 0, skipped: 0, failed: 0 };
  } catch (e: any) {
    return fail(e, "Applying the surcharge");
  }
};

/** The per-quote switch. */
export const setQuoteSurcharge = async (quoteId: string, on: boolean): Promise<any | Err> => {
  try {
    const res = await axios.put(`/fuel-surcharge/quotes/${quoteId}`, { on });
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, on ? "Adding the fuel surcharge" : "Removing the fuel surcharge");
  }
};

/** The per-invoice switch (unpaid invoices only). Removing needs a reason; the response lists POs on the job that still carry a surcharge. */
export const setInvoiceSurcharge = async (invoiceId: string, on: boolean, reason?: string): Promise<any | Err> => {
  try {
    const res = await axios.put(`/invoices/${invoiceId}/fuel-surcharge`, { on, reason });
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, on ? "Adding the fuel surcharge" : "Removing the fuel surcharge");
  }
};

/** Provider side on a PO, after acceptance: refresh to the provider's current rate, or remove. Refused once billed. */
export const setPurchaseOrderSurcharge = async (poId: string, mode: "refresh" | "remove", reason: string): Promise<any | Err> => {
  try {
    const res = await axios.put(`/purchase-orders/${poId}/fuel-surcharge`, { mode, reason });
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, mode === "refresh" ? "Refreshing the PO surcharge" : "Removing the PO surcharge");
  }
};

/** For the Instant Estimate notice. Never throws; off on any error. */
export const getFuelSurchargeActive = async (): Promise<boolean> => {
  try {
    const res = await axios.get(`/fuel-surcharge/active`, { excludeInterceptor: true } as any);
    return !!res?.data?.active;
  } catch {
    return false;
  }
};
